import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    styled,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { useMortgageState } from "../../../../contexts/mortgage/Reducer";
import { DateFormat, formatDate } from "../../../../utils/DateUtils";
import { formatAmount } from "../../../../utils/NumberUtils";
import AddressInfo from "../../../address-info/AddressInfo";
import { CloseButton, CloseIconButton, TitleTypography } from "../life-insurance/LifeInsurancePopupScreen";
import MortgageDetailSummaryDesktop from "./desktop/MortgageDetailSummaryDesktop";
import MortgageDetailSummaryMobile from "./mobile/MortgageDetailSummaryMobile";
import MortgageTotalLoadGraphic from "./MortgageTotalLoadGraphic";

const DetailDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogTitle-root": {
        marginTop: "var(--lukb-portal-safe-area-inset-top, 0px)",
        marginBottom: "24px",
        padding: "16px",
        marginLeft: "var(--lukb-portal-safe-area-inset-left, 0px)",
        marginRight: "var(--lukb-portal-safe-area-inset-right, 0px)",
    },
    "& .MuiDialogContent-root": {
        paddingLeft: "16px",
        paddingRight: "14px",
        paddingTop: "0px",
        marginLeft: "var(--lukb-portal-safe-area-inset-left, 0px)",
        marginRight: "var(--lukb-portal-safe-area-inset-right, 0px)",
    },
    "& .MuiDialogActions-root": {
        padding: "16px",
        marginBottom: "var(--lukb-portal-safe-area-inset-bottom, 0px)",
        marginLeft: "var(--lukb-portal-safe-area-inset-left, 0px)",
        marginRight: "var(--lukb-portal-safe-area-inset-right, 0px)",
    },

    [theme.breakpoints.up("lg")]: {
        "& .MuiDialog-paper": {
            width: "100%",
            maxWidth: "1100px",
            margin: "0px",
            paddingLeft: "0px",
            paddingRight: "0px",
        },
        "& .MuiDialogTitle-root": {
            paddingTop: "40px",
            marginBottom: "48px",
            textAlign: "center",
            marginTop: "0px",
            marginLeft: "0px",
            marginRight: "0px",
        },
        "& .MuiDialogContent-root": {
            paddingLeft: "60px",
            paddingRight: "60px",
            marginLeft: "0px",
            marginRight: "0px",
        },
        "& .MuiDialogActions-root": {
            padding: "8px",
            paddingBottom: "40px",
            marginBottom: "0px",
            marginLeft: "0px",
            marginRight: "0px",
        },
    },
}));

const DetailsPopupScreen = (props: { open: boolean; closeDialogFunction: () => void; fullScreen?: boolean }) => {
    const { open, closeDialogFunction, fullScreen } = props;
    const { t } = useTranslation();
    const { newMortgage, mortgage } = useMortgageState();
    const theme = useTheme();
    const matchesWebView = useMediaQuery(theme.breakpoints.up("lg"));

    if (open) {
        document.body.style.overflowY = "hidden";
    } else {
        document.body.style.overflowY = "unset";
    }

    const renderMortgages = () => {
        if (matchesWebView) {
            return <MortgageDetailSummaryDesktop />;
        } else {
            return <MortgageDetailSummaryMobile />;
        }
    };

    return (
        <DetailDialog
            onClose={closeDialogFunction}
            open={open}
            scroll={"body"}
            fullScreen={fullScreen}
            data-cy={"details-popup-screen"}>
            <DialogTitle align={"center"} sx={{ position: "relative" }}>
                <TitleTypography>{t("ConfigurationScreen.Total charge in detail")}</TitleTypography>
                <CloseIconButton
                    sx={{
                        position: "absolute",
                        top: "14px",
                        right: "14px",
                        margin: "0px",
                        padding: "0px",
                        minWidth: "0px",
                    }}
                    aria-label={t("Actions.Close")}
                    onClick={closeDialogFunction}>
                    <CloseOutlinedIcon sx={{ width: "32px", height: "32px" }} />
                </CloseIconButton>
            </DialogTitle>

            <DialogContent sx={{ overflowY: "unset" }}>
                {mortgage && <AddressInfo selectedMortgage={mortgage} />}
                <Typography variant={"body1"}>
                    {t("ConfigurationScreen.Your mortgage expires on", {
                        currency: newMortgage?.currency ?? "",
                        expirationDate: formatDate(newMortgage?.expirationDate, DateFormat.DATE) ?? "",
                        amount: formatAmount(newMortgage?.amount) ?? "",
                    })}
                </Typography>

                {matchesWebView && <MortgageTotalLoadGraphic chosenMortgage={mortgage!} newMortgage={newMortgage!} />}
                {renderMortgages()}
            </DialogContent>
            <DialogActions disableSpacing={true}>
                <CloseButton variant="contained" onClick={closeDialogFunction}>
                    {t("Actions.Close")}
                </CloseButton>
            </DialogActions>
        </DetailDialog>
    );
};

export default DetailsPopupScreen;
